import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import HintBlock from '../../../common/HintBlock';
import AddNoteButton from '../AddNoteButton';
import NodeParentChanger from '../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../NoteForm';
import ScenarioSide from '../scenario/text-scenario/scenario-side';
import VideoScenarioDeleteButton from '../scenario/video-scenario/video-scenario-delete-button';
import ScenarioHeader from '../ScenarioHeader';
import { MacBotActionKind } from '../types';

interface IFormData extends INoteForm {
  title: string;
  additionalFields?: null | {
    birthDate: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
    countryCode: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
    firstName: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
    lastName: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
    email: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
    password: {
      isActive: boolean;
      isRequired: boolean;
      type: string;
    };
  };
}

export const EditRegisterUserForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(true);
  const userId = useAppSelector(selectUseId);

  const variableFields = [
    { name: t('SuperUserManager/email'), key: 'email', type: 'string' },
    { name: t('SuperUserManager/password'), key: 'password', type: 'string' },
    { name: t('SuperUserManager/firstName'), key: 'firstName', type: 'string' },
    { name: t('SuperUserManager/lastName'), key: 'lastName', type: 'string' },
    { name: t('SuperUserManager/birthDate'), key: 'birthDate', type: 'date' },
    {
      name: t('SuperUserManager/countryCode'),
      key: 'countryCode',
      type: 'string',
    },
  ];

  const schema = Yup.object({
    title: Yup.string().max(30, t('SuperUserManager/blockTitleMaxLength')).required(t('SuperUserManager/blockTitleRequired')),
    additionalFields: Yup.object().shape(
      variableFields.reduce((acc, field) => {
        const defaultValue = field.key === 'email' || field.key === 'password';
        acc[field.key] = Yup.object({
          isActive: Yup.boolean().when('action', (action, schema) =>
            action === 'INJECT_TEMPLATE' ? schema.required().default(defaultValue) : schema.notRequired().nullable()
          ),
          isRequired: Yup.boolean().when('action', (action, schema) =>
            action === 'INJECT_TEMPLATE' ? schema.required().default(defaultValue) : schema.notRequired().nullable()
          ),
          type: Yup.string().required().default(field.type),
        });

        return acc;
      }, {})
    ),
    ...getNoteSchema(t),
  });
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    control,
    getValues,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: 'Register user',
      additionalFields: {
        email: { isActive: true },
        password: { isActive: true },
        birthDate: { isActive: false },
        countryCode: { isActive: false },
        firstName: { isActive: false },
        lastName: { isActive: false },
      },
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    const values = {};
    variableFields.map(item => {
      if (item.name != 'Email' && item.name != 'Password') {
        const isActiveValue = watch(`additionalFields.${item.key}.isActive`);
        values[item.key] = {
          isActive: isActiveValue,
          isRequired: getValues(`additionalFields.${item.key}.isRequired`) || false,
          type: item.key == 'birthDate' ? 'date' : 'text',
        };
      }
    });
    if (itemData) {
      const _data = {
        id: itemData.id,
        type: BlockType.InjectTemplate,
        parent: itemData.parent,
        title: data.title,
        notes: data.notes,
        block_id: itemData.block_id,
        additionalFields: values,
      };
      changeScenarioBlock(_data).then(() => {
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        additionalFields: variableFields.reduce((acc, field) => {
          acc[field.key] = {
            isActive: itemData?.additionalFields?.[field.key]?.isActive || false,
            isRequired: itemData?.additionalFields?.[field.key]?.isRequired || false,
            type: itemData?.additionalFields?.[field.key]?.type || field.type,
          };
          return acc;
        }, {}),
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowRegisterUser}
          onClose={() => {
            reset();
          }}
        />
        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Col xs={12}>
            <Form.Group>
              <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('SuperUserManager/blockTitle')}
              </Form.Label>
              <Form.Control
                maxLength={31}
                max={31}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                isInvalid={!!errors.title}
                {...register(`title`)}
                disabled
              />
              <HintBlock texts={[t('SuperUserManager/blockTitleMaxHint')]} />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <div className="tw-h-px tw-border tw-border-solid tw-border-gray-200"></div>

          <div className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-mt-4">{t('SuperUserManager/templateHint')}</div>
          <div className=" tw-w-96 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex tw-w-full tw-mt-4  tw-pb-10">
            {variableFields.map(({ name, key }) => (
              <div key={key} className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                {/* Checkbox */}
                <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
                  <Controller
                    name={`additionalFields.${key}.isActive`}
                    control={control}
                    defaultValue={key === 'email' || key === 'password'}
                    render={({ field: { value, onChange, ...field } }) => (
                      <Form.Check
                        {...field}
                        type="checkbox"
                        label={name}
                        id={`additionalFields.${key}.isActive`}
                        disabled={key === 'email' || key === 'password'}
                        checked={key === 'email' || key === 'password' ? true : value || false}
                        onChange={e => {
                          onChange(e.target.checked);
                          if (!e.target.checked) setValue(`additionalFields.${key}.isRequired`, false);
                        }}
                      />
                    )}
                  />
                </div>

                {/* Switch */}
                <Form.Group>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-gap-4 tw-items-center tw-mr-2">
                      <Form.Switch
                        disabled={key === 'email' || key === 'password' || !watch(`additionalFields.${key}.isActive`)}
                        defaultChecked={key === 'email' || key === 'password'}
                        {...register(`additionalFields.${key}.isRequired`)}
                      />
                    </div>
                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('SuperUserManager/required')}</div>
                  </div>
                </Form.Group>
              </div>
            ))}
          </div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-edit-register-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
